import React, { useEffect, useState } from "react";
import {
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	TableFooter,
} from "@mui/material";
import { useCentralStore } from "@/dentlab/src/store/Central";
import {
	JobDocumentEntityType,
	JobItemEntityType,
	JobEntityType,
} from "@/lib/supabase/supabaseTypes";
import { JobItemsTotalsPopover } from "./JobItemsTotalsPopover";
import { buildTotalRows } from "@/dentlab/src/pages/job-page/job-document/job-document-components/total-sidebar.component";
import { calculateJobItemAmountForJobItem } from "@/dentlab/src/lib/utils/calculate";
import { supabase } from "@/lib/supabase/supabaseClient";
import { LsStatusEnum } from "@/dentlab/src/hooks/actions/actions-hooks-types";
import { Logger } from "@/lib/logger/Logger";
import { JobDocumentTypeEnum } from "@/dentlab/src/pages/job-page/job-document/job-document.types";
import { TechnicianHoverMenu } from "./TechnicianHoverMenu.component";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTriggerStandard,
} from "@/dentlab/src/components/ui/collapsible";
import { ClipboardPaste } from "lucide-react";

type JobDocumentWithItems = JobDocumentEntityType & {
	job_items: JobItemEntityType[];
};

export const JobItemsPreviewTable: React.FC<{ job: JobEntityType }> = ({
	job,
}) => {
	const { organization, clientsLookup } = useCentralStore((state) => ({
		organization: state.organization,
		clientsLookup: state.clientsLookup,
	}));
	const client = job.client_id ? clientsLookup[job.client_id] : null;
	const [jobDocument, setJobDocument] = useState<JobDocumentWithItems | null>(
		null
	);
	const [isLieferscheinOpen, setIsLieferscheinOpen] = useState(true);

	const fetchDeliveryNote = async (jobId: number) => {
		const { data, error } = await supabase
			.from("job_documents")
			.select("*, job_items(*)")
			.eq("job_id", jobId)
			.eq("type", JobDocumentTypeEnum.DeliveryNote)
			.not("status", "eq", LsStatusEnum.ARCHIVED)
			.order("created_at", { ascending: false })
			.limit(1)
			.single();
		if (error) {
			Logger.error(error);
			return null;
		}
		setJobDocument(data);
	};

	useEffect(() => {
		if (job.id) {
			fetchDeliveryNote(Number(job.id));
		}
	}, [job.id]);

	if (!organization) {
		return (
			<div className="text-gray-500 text-sm p-4 text-center">
				Keine Organisation
			</div>
		);
	}
	if (!client) {
		return (
			<div className="text-gray-500 text-sm p-4 text-center">
				Kein Kunde gefunden
			</div>
		);
	}
	if (!jobDocument) {
		return (
			<div className="text-gray-500 text-sm p-4 text-center">
				Keine Lieferscheine
			</div>
		);
	}

	const items: JobItemEntityType[] = jobDocument.job_items || [];
	const { rows, totalText } = buildTotalRows({
		jobItems: items,
		jobDocument: jobDocument,
		job,
		organization,
		client,
	});

	if (items.length === 0) {
		return (
			<div className="text-gray-500 text-sm p-4 text-center">
				Keine Positionen
			</div>
		);
	}

	return (
		<Collapsible
			onOpenChange={setIsLieferscheinOpen}
			open={isLieferscheinOpen}
			className="border-b-0"
		>
			<CollapsibleTriggerStandard
				open={isLieferscheinOpen}
				icon={<ClipboardPaste fontSize="small" />}
			>
				{jobDocument.title} - Positionen ansehen
			</CollapsibleTriggerStandard>
			<CollapsibleContent>
				<Table
					size="small"
					style={{ tableLayout: "fixed", width: "100%" }}
				>
					<TableHead>
						<TableRow>
							<TableCell
								style={{
									width: "20px",
								}}
							></TableCell>
							<TableCell
								style={{
									fontWeight: 600,
									width: "30px",
								}}
							>
								Code
							</TableCell>
							<TableCell
								style={{
									fontWeight: 600,
									width: "50px",
								}}
							>
								LOT
							</TableCell>
							<TableCell
								style={{
									fontWeight: 600,
									width: "30px",
									textAlign: "right",
								}}
							>
								Menge
							</TableCell>
							<TableCell
								style={{
									fontWeight: 600,
									width: "30px",

									textAlign: "right",
								}}
							>
								TPW
							</TableCell>
							<TableCell
								style={{
									fontWeight: 600,
									width: "30px",

									textAlign: "right",
								}}
							>
								Preis/E
							</TableCell>
							<TableCell
								style={{
									fontWeight: 600,
									width: "50px",
									textAlign: "right",
								}}
							>
								Betrag
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{items.map((item) => (
							<TableRow key={item.id} hover>
								<TableCell>
									<TechnicianHoverMenu jobItem={item} />
								</TableCell>
								<TableCell>{item.code}</TableCell>
								<TableCell>{item.lot ?? ""}</TableCell>
								<TableCell style={{ textAlign: "right" }}>
									{item.quantity}
								</TableCell>
								<TableCell style={{ textAlign: "right" }}>
									{item.tp_value ?? ""}
								</TableCell>

								<TableCell style={{ textAlign: "right" }}>
									{item.price ?? ""}
								</TableCell>
								<TableCell style={{ textAlign: "right" }}>
									{calculateJobItemAmountForJobItem(
										item
									).toFixed(2)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
					<TableFooter>
						<TableRow>
							<TableCell
								colSpan={6}
								style={{
									textAlign: "right",
									fontWeight: 600,
								}}
							>
								Subtotal
							</TableCell>
							<TableCell
								style={{
									textAlign: "right",
									fontWeight: 600,
								}}
							>
								{
									rows.find((row) => row.name === "Subtotal")
										?.value
								}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell
								colSpan={6}
								style={{
									textAlign: "right",
									fontWeight: 600,
								}}
							>
								MwSt.
							</TableCell>
							<TableCell
								style={{
									textAlign: "right",
									fontWeight: 600,
								}}
							>
								{
									rows.find((row) => row.name === "MwSt.")
										?.value
								}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell
								colSpan={6}
								style={{
									textAlign: "right",
									fontWeight: 600,
								}}
							>
								Total
							</TableCell>
							<TableCell
								style={{
									textAlign: "right",
									fontWeight: 600,
								}}
							>
								<JobItemsTotalsPopover
									rows={rows}
									totalText={totalText}
								/>
							</TableCell>
						</TableRow>
					</TableFooter>
				</Table>
			</CollapsibleContent>
		</Collapsible>
	);
};
